import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, Folder, FolderOpen, FileText, Plus, Pencil, Trash2, FolderInput, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';

export type TreeNode = KnowledgeBaseItem & { children: TreeNode[] };

export function buildTree(items: KnowledgeBaseItem[]): TreeNode[] {
  const itemMap = new Map<string, TreeNode>();
  for (const item of items) {
    itemMap.set(item.id, { ...item, children: [] });
  }

  const roots: TreeNode[] = [];
  for (const node of itemMap.values()) {
    if (node.parent_id && itemMap.has(node.parent_id)) {
      itemMap.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.item_type !== b.item_type) return a.item_type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach(n => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

interface FolderTreeProps {
  items: KnowledgeBaseItem[];
  selectedId: string | null;
  onSelect: (item: KnowledgeBaseItem) => void;
  onCreateFolder: (parentId: string | null) => void;
  onCreateArticle: (parentId: string | null) => void;
  onRename: (item: KnowledgeBaseItem) => void;
  onMove: (item: KnowledgeBaseItem) => void;
  onDelete: (item: KnowledgeBaseItem) => void;
}

function TreeNodeItem({
  node,
  depth,
  selectedId,
  expandedIds,
  toggleExpanded,
  onSelect,
  onCreateFolder,
  onCreateArticle,
  onRename,
  onMove,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
  onSelect: (item: KnowledgeBaseItem) => void;
  onCreateFolder: (parentId: string | null) => void;
  onCreateArticle: (parentId: string | null) => void;
  onRename: (item: KnowledgeBaseItem) => void;
  onMove: (item: KnowledgeBaseItem) => void;
  onDelete: (item: KnowledgeBaseItem) => void;
}) {
  const isFolder = node.item_type === 'folder';
  const isPending = node.status === 'pending';
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer text-sm hover:bg-muted',
          isSelected && 'bg-muted font-medium',
          isPending && 'opacity-70'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          if (isFolder) {
            toggleExpanded(node.id);
          }
          onSelect(node);
        }}
      >
        {isFolder ? (
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}

        {isFolder ? (
          isExpanded ? (
            <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
          )
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        <span className="truncate flex-1">{node.name}</span>

        {isPending && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-600 border-amber-400">
            <Clock className="h-2.5 w-2.5 mr-0.5" />
            Pending
          </Badge>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {isFolder && (
              <>
                <DropdownMenuItem onClick={() => onCreateArticle(node.id)}>
                  <FileText className="h-4 w-4 mr-2" />
                  New Article
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCreateFolder(node.id)}>
                  <Folder className="h-4 w-4 mr-2" />
                  New Subfolder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => onRename(node)}>
              <Pencil className="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMove(node)}>
              <FolderInput className="h-4 w-4 mr-2" />
              Move
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onDelete(node)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete{isFolder ? ' Folder' : ''}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isFolder && isExpanded && node.children.length > 0 && (
        <div>
          {node.children.map(child => (
            <TreeNodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              toggleExpanded={toggleExpanded}
              onSelect={onSelect}
              onCreateFolder={onCreateFolder}
              onCreateArticle={onCreateArticle}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTree({
  items,
  selectedId,
  onSelect,
  onCreateFolder,
  onCreateArticle,
  onRename,
  onMove,
  onDelete,
}: FolderTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildTree(items), [items]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-0.5 py-1">
      {tree.length === 0 && (
        <p className="text-sm text-muted-foreground px-4 py-8 text-center">
          No items yet. Create a folder or article to get started.
        </p>
      )}
      {tree.map(node => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          expandedIds={expandedIds}
          toggleExpanded={toggleExpanded}
          onSelect={onSelect}
          onCreateFolder={onCreateFolder}
          onCreateArticle={onCreateArticle}
          onRename={onRename}
          onMove={onMove}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
